/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { Box, SpeedDial, SpeedDialAction, Theme, Typography } from '@mui/material';
import { shallow } from 'zustand/shallow';

import { Check, Eye, Times } from '@assets/svgs';
import { useStore } from '@store/GlobalStore';
import { ProductOfferingId } from '@models/newQuote';
import { TQuoteApiResponse } from '@models/quotes';
import { QuoteStatus } from '@lio/rule-engine';
import { useSearchParams } from 'react-router-dom';
import { getTodaysHoliday } from '@theme/palettes/getPalette';
import { adjustFontSize } from '@theme/styleOverrides/typography';
import { useQuoteStore } from '@store/Quote/QuoteStore';

export type ImpersonationSpeedDialControlProps = {
  offeringId: string;
  quoteStatus: TQuoteApiResponse['status'];
  customerSubmittedOn?: Date;
};

export const ImpersonationSpeedDialControl = ({
  offeringId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  customerSubmittedOn, // leaving for now incase we add back later
}: ImpersonationSpeedDialControlProps) => {
  const [searchParamsInTheUrl, setSearchParamsInTheUrl] = useSearchParams();
  const [roles, userAuth, setUserRoles, impersonation] = useStore(
    (state) => [state.roles, state.userAuth, state.setUserRoles, state.impersonation],
    shallow,
  );
  const [triggerValidation] = useQuoteStore((state) => [state.triggerValidation], shallow);

  const canImpersonate =
    userAuth.isUnderwriter || userAuth.isPortalAdmin || userAuth.isAdmin || userAuth.isJrOps || userAuth.isSrOps;

  const [selectedView, setSelectedView] = useState<string | undefined>();

  const userOriginalAuthState = useMemo(() => {
    return { roles, userAuth };
  }, []);

  useEffect(() => {
    if (selectedView && !impersonation) {
      setSelectedView(undefined);
    }
  }, [impersonation]);

  useEffect(() => {
    return () => {
      // when the user leaves the page, clear the impersonation
      if (impersonation) {
        setSelectedView(undefined);
        setUserRoles({ ...impersonation.userOriginalAuthState, impersonation: undefined });
      }
    };
  }, []);

  if ((!selectedView && !canImpersonate) || offeringId === ProductOfferingId.GenericLinesProduct) {
    return null;
  }

  // Determine the right position based on the URL (localhost has a react-query devtool widget so we have to scoot it over more)
  let rightPosition = window.location.href.includes('localhost') ? 80 : 16;
  if (getTodaysHoliday()) {
    rightPosition += 50;
  }

  const ViewOptions = [
    {
      label: 'Producer',
      value: 'producer',
    },
    {
      label: 'Policyholder',
      value: 'policyholder',
      hidden: ![ProductOfferingId.SpecialEvent as string, ProductOfferingId.SwimClub as string].includes(offeringId),
    },
  ];

  const handleSelectView = (value: string) => {
    if (value === selectedView) {
      setSelectedView(undefined);
      searchParamsInTheUrl.delete('viewReadOnly');
      setSearchParamsInTheUrl(searchParamsInTheUrl);
      setUserRoles({ ...userOriginalAuthState, impersonation: undefined });
    } else {
      if (value === 'policyholder') {
        setUserRoles({
          ...PolicyHolderAuthState,
          impersonation: { userOriginalAuthState, viewingAs: 'Policy Holder' },
        });
      }
      if (value === 'producer') {
        const appendReadonly = [
          QuoteStatus.Bound,
          QuoteStatus.Referred,
          QuoteStatus.Rejected,
          QuoteStatus.Declined,
          QuoteStatus.Expired,
          QuoteStatus.Canceled,
        ];

        if (appendReadonly) {
          searchParamsInTheUrl.set('viewReadOnly', 'true');
          setSearchParamsInTheUrl(searchParamsInTheUrl);
        }

        setUserRoles({
          ...ProducerAuthState,
          impersonation: { userOriginalAuthState, viewingAs: 'Producer' },
        });
      }

      setSelectedView(value);
    }
    triggerValidation();
  };

  return (
    <SpeedDial
      data-testid='impersonation-speed-dial'
      ariaLabel='View Controls'
      sx={{
        position: 'fixed',
        bottom: 16,
        right: rightPosition,

        width: 40,
        height: 40,
        '& .MuiFab-primary': {
          backgroundColor: selectedView ? 'primary.main' : 'gray',
          width: 40,
          height: 40,
        },
      }}
      icon={<Eye />}
    >
      {ViewOptions.filter((x) => !x.hidden).map((option) => {
        const isSelected = option.value === selectedView;
        return (
          <SpeedDialAction
            key={option.value}
            aria-label={`Select view as ${option.label}`}
            icon={
              isSelected ? (
                <Box
                  sx={{
                    mt: '6px',
                  }}
                >
                  <Times width='14px' height='14px' />
                </Box>
              ) : (
                <Box
                  sx={{
                    mt: '6px',
                  }}
                >
                  <Check width='14px' height='14px' />
                </Box>
              )
            }
            tooltipTitle={
              isSelected ? (
                <Typography
                  sx={{
                    ...sxProps.tooltip,
                    backgroundColor: '#1C2D40',
                    color: 'white',
                  }}
                >
                  Viewing as {option.label}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    ...sxProps.tooltip,
                    color: '#1C2D40',
                    backgroundColor: 'white',
                    border: `1px solid gray`,
                    // borderColor: 'button.outlinedDark',
                    // color: 'button.outlinedDark',
                  }}
                >
                  View as {option.label}
                </Typography>
              )
            }
            tooltipOpen
            onClick={() => {
              handleSelectView(option.value);
            }}
            sx={() => {
              return {
                '& .MuiFab-root': {
                  color: isSelected ? '#1C2D40' : 'gray',
                  backgroundColor: isSelected ? '#E5E5E5' : 'white',

                  ':hover': {
                    backgroundColor: isSelected ? '#C6C6C2' : '#E5E5E5',
                  },
                },
                '.MuiSpeedDialAction-staticTooltipLabel': {
                  background: 'transparent',

                  boxShadow: 'none',
                  marginRight: '0px',
                  paddingRight: 0,
                  textAlign: 'right',
                  width: 200,
                },
              };
            }}
          />
        );
      })}
    </SpeedDial>
  );
};

const PolicyHolderAuthState = {
  roles: ['Quoter'],
  userAuth: {
    isAdmin: false,
    isPortalAdmin: false,
    isUnderwriter: false,
    isBroker: false,
    isJrOps: false,
    isSrOps: false,
    isLioSales: false,
    isInternalUser: false,
    isCSR: false,
    isCustomerUser: true,
    isAuthenticated: true,
    isLoading: false,
    isError: false,
  },
};

const ProducerAuthState = {
  roles: ['Producer'],
  userAuth: {
    isAdmin: false,
    isPortalAdmin: false,
    isUnderwriter: false,
    isBroker: true,
    isJrOps: false,
    isSrOps: false,
    isLioSales: false,
    isInternalUser: false,
    isCSR: false,
    isCustomerUser: false,
    isAuthenticated: true,
    isLoading: false,
    isError: false,
  },
};

const sxProps = {
  tooltip: {
    boxShadow: '2px 6px 12px 0px rgba(0,0,0,.3)',
    width: 180,
    fontSize: ({ palette }: Theme) => `${adjustFontSize(14, palette.fontSize)}px`,
    textAlign: 'center',
    borderRadius: '20px',
    paddingY: '6px',
    paddingX: 0,
  },
};
